/**
 * Offline-first Task Service
 *
 * Handles creating, storing, and syncing tasks when offline.
 * Uses IndexedDB for local storage and syncs to the server when online.
 */

import { db, CachedTask } from './db';
import { taskService } from '@/services/task.service';
import { networkService } from '@/services/network.service';
import { useTenantStore } from '@/store/tenant.store';
import { CreateTaskData, TaskPriority, TaskStatus } from '@/types/task';
import { clientIdFor, isDeletedReplay, originatorStamp, mustDeferSend, sendTenantOf } from './client-id';
import { resolveSyncableIncidentId } from './incident-offline.service';

export interface TaskInput {
  incident_id?: string | null;
  task_type_id?: string;
  title: string;
  description?: string;
  priority?: TaskPriority;
  assigned_to?: string;
  assigned_esf_id?: string;
  // Unified org tree assignment (incident_org_nodes)
  org_node_id?: string | null;
  location_name?: string;
  location_address?: string;
  location_coordinates?: { lat: number; lng: number };
  due_at?: string;
  custom_fields?: Record<string, unknown>;
  requestor_name?: string;
  requestor_phone?: string;
  requestor_email?: string;
  tags?: string[];
  source_module?: string;
  source_id?: string;
  parent_task_id?: string;
}

export interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors: string[];
}

class TaskOfflineService {
  /**
   * Create a new task (works offline)
   */
  async create(
    tenantId: string,
    input: TaskInput
  ): Promise<CachedTask> {
    // P2-05: the client id becomes the server task's id (idempotent create),
    // and the item records who created it, in which tenant and device session.
    const stamp = originatorStamp(tenantId);
    const offlineId = `offline_${stamp.client_id}`;
    const now = stamp.occurred_at;

    const task: CachedTask = {
      ...stamp,
      id: offlineId,
      offline_id: offlineId,
      tenant_id: tenantId,
      incident_id: input.incident_id || null,
      title: input.title,
      description: input.description,
      status: 'open',
      priority: input.priority || 'medium',
      assigned_to: input.assigned_to,
      assigned_esf_id: input.assigned_esf_id,
      org_node_id: input.org_node_id,
      task_type_id: input.task_type_id,
      location: input.location_coordinates
        ? { lat: input.location_coordinates.lat, lon: input.location_coordinates.lng }
        : undefined,
      location_name: input.location_name,
      location_address: input.location_address,
      due_at: input.due_at,
      custom_fields: input.custom_fields || {},
      requestor_name: input.requestor_name,
      requestor_phone: input.requestor_phone,
      requestor_email: input.requestor_email,
      tags: input.tags,
      parent_task_id: input.parent_task_id,
      source_module: input.source_module,
      source_id: input.source_id,
      created_at: now,
      updated_at: now,
      is_synced: 0,
      pending_changes: true,
    };

    await db.cachedTasks.add(task);

    // Try to sync immediately if online
    if (networkService.isOnline()) {
      await this.syncTask(offlineId);
    }

    return task;
  }

  /**
   * Update an existing cached task (works offline)
   */
  async update(
    taskId: string,
    updates: Partial<TaskInput>
  ): Promise<CachedTask | null> {
    const existing = await this.getById(taskId);
    if (!existing) return null;

    const updated: CachedTask = {
      ...existing,
      title: updates.title ?? existing.title,
      description: updates.description ?? existing.description,
      priority: updates.priority ?? existing.priority,
      assigned_to: updates.assigned_to ?? existing.assigned_to,
      assigned_esf_id: updates.assigned_esf_id ?? existing.assigned_esf_id,
      org_node_id: updates.org_node_id ?? existing.org_node_id,
      task_type_id: updates.task_type_id ?? existing.task_type_id,
      location: updates.location_coordinates
        ? { lat: updates.location_coordinates.lat, lon: updates.location_coordinates.lng }
        : existing.location,
      location_name: updates.location_name ?? existing.location_name,
      location_address: updates.location_address ?? existing.location_address,
      due_at: updates.due_at ?? existing.due_at,
      custom_fields: updates.custom_fields ?? existing.custom_fields,
      requestor_name: updates.requestor_name ?? existing.requestor_name,
      requestor_phone: updates.requestor_phone ?? existing.requestor_phone,
      requestor_email: updates.requestor_email ?? existing.requestor_email,
      tags: updates.tags ?? existing.tags,
      parent_task_id: updates.parent_task_id ?? existing.parent_task_id,
      updated_at: new Date().toISOString(),
      is_synced: 0,
      pending_changes: true,
      // PATCH /tasks/:id does not accept assigned_to (it needs POST
      // /tasks/:id/assign), so an offline reassignment must be replayed
      // separately by syncTask or it is silently discarded.
      pending_assignment:
        existing.pending_assignment ||
        (updates.assigned_to !== undefined && updates.assigned_to !== existing.assigned_to),
    };

    await db.cachedTasks.put(updated);

    if (networkService.isOnline()) {
      await this.syncTask(updated.id);
    }

    return updated;
  }

  /**
   * Get all cached tasks for a tenant, optionally filtered by incident
   */
  async getAll(tenantId: string, incidentId?: string | null): Promise<CachedTask[]> {
    const results = await db.cachedTasks
      .where('tenant_id')
      .equals(tenantId)
      .toArray();

    if (incidentId !== undefined) {
      return results.filter(t => t.incident_id === incidentId);
    }

    return results;
  }

  /**
   * Quick status update for a cached task (works offline)
   */
  async updateStatus(
    taskId: string,
    status: TaskStatus,
    notes?: string
  ): Promise<CachedTask | null> {
    const existing = await this.getById(taskId);
    if (!existing) return null;

    const updated: CachedTask = {
      ...existing,
      status,
      updated_at: new Date().toISOString(),
      is_synced: 0,
      pending_changes: true,
      // Record the intent so a later syncTask() can replay it. syncTask's
      // update path calls PATCH /tasks/:id, whose schema does NOT accept
      // `status` — without this the change is dropped and the row is then
      // marked synced, permanently losing a field responder's completion.
      pending_status: status,
      pending_status_notes: notes,
    };

    await db.cachedTasks.put(updated);

    // Try to sync the status change if online, and only when the pending change is this
    // session's to send (a change owned by its first writer waits for them).
    if (networkService.isOnline() && !mustDeferSend(updated)) {
      try {
        // If the task has a server ID, push status directly
        if (existing.id && !existing.id.startsWith('offline_')) {
          await taskService.updateTaskStatus(existing.id, { status, notes }, sendTenantOf(updated));
          await db.cachedTasks.update(existing.id, {
            is_synced: 1,
            synced_at: new Date().toISOString(),
            pending_changes: false,
            pending_status: undefined,
            pending_status_notes: undefined,
            last_error: undefined,
          });
        } else {
          // Offline-created task, sync the whole thing
          await this.syncTask(existing.id);
        }
      } catch (error) {
        console.warn('[TaskOffline] Status sync failed, will retry later:', error);
      }
    }

    return updated;
  }

  /**
   * Soft-delete a cached task (marks for deletion, syncs when online)
   */
  async markForDeletion(taskId: string): Promise<boolean> {
    const existing = await this.getById(taskId);
    if (!existing) return false;

    // If the task was only created offline and never synced, just remove it
    if (existing.offline_id && existing.id === existing.offline_id) {
      await db.cachedTasks.delete(existing.id);
      return true;
    }

    // Mark as deleted locally -- will sync a server DELETE when online.
    // pending_delete is the tombstone that drives syncTask to issue the DELETE
    // and prevents cacheFromServer from resurrecting the row.
    await db.cachedTasks.update(existing.id, {
      status: 'cancelled',
      updated_at: new Date().toISOString(),
      is_synced: 0,
      pending_changes: true,
      pending_delete: true,
    });

    return true;
  }

  /**
   * Get a single task by ID or offline_id
   */
  async getById(taskId: string): Promise<CachedTask | undefined> {
    let task = await db.cachedTasks.get(taskId);
    if (!task) {
      task = await db.cachedTasks
        .where('offline_id')
        .equals(taskId)
        .first();
    }
    return task;
  }

  /**
   * Get a task by its server UUID
   */
  async getByServerId(serverId: string): Promise<CachedTask | undefined> {
    return db.cachedTasks.get(serverId);
  }

  /**
   * Get pending (unsynced) tasks
   */
  async getPending(): Promise<CachedTask[]> {
    return db.cachedTasks
      .where('is_synced')
      .equals(0) // Dexie stores booleans as 0/1
      .toArray();
  }

  /**
   * Get count of pending items
   */
  async getPendingCount(): Promise<number> {
    return db.cachedTasks
      .filter(t => t.is_synced === 0)
      .count();
  }

  /**
   * Alias for getPendingCount for API consistency
   */
  async getUnsyncedCount(): Promise<number> {
    return this.getPendingCount();
  }

  /**
   * Cache tasks from the server for offline access.
   * Conflict resolution:
   * - Local pending changes take priority over server state
   * - If local is synced and server is newer, update from server
   * - If local is synced and server is same/older, keep local
   */
  async cacheFromServer(
    tenantId: string,
    incidentId?: string
  ): Promise<{ cached: number; conflicts: number }> {
    const result = { cached: 0, conflicts: 0 };

    try {
      const res = await taskService.getTasks({
        incident_id: incidentId,
        limit: 100,
      });
      const serverTasks = res.data || [];

      for (const serverTask of serverTasks) {
        const existing = await db.cachedTasks.get(serverTask.id);

        const serverCached: CachedTask = {
          id: serverTask.id,
          tenant_id: tenantId,
          incident_id: serverTask.incident_id,
          title: serverTask.title,
          description: serverTask.description || undefined,
          status: serverTask.status,
          priority: serverTask.priority,
          assigned_to: serverTask.assigned_to || undefined,
          assigned_esf_id: serverTask.assigned_esf_id || undefined,
          org_node_id: serverTask.org_node_id || undefined,
          task_type_id: serverTask.task_type_id || undefined,
          location: serverTask.location_coordinates
            ? { lat: serverTask.location_coordinates.lat, lon: serverTask.location_coordinates.lng }
            : undefined,
          location_name: serverTask.location_name || undefined,
          location_address: serverTask.location_address || undefined,
          due_at: serverTask.due_at || undefined,
          custom_fields: serverTask.custom_fields || {},
          requestor_name: serverTask.requestor_name || undefined,
          requestor_phone: serverTask.requestor_phone || undefined,
          requestor_email: serverTask.requestor_email || undefined,
          tags: serverTask.tags || undefined,
          parent_task_id: serverTask.parent_task_id || undefined,
          created_at: serverTask.created_at,
          updated_at: serverTask.updated_at,
          is_synced: 1,
          synced_at: new Date().toISOString(),
          pending_changes: false,
        };

        if (!existing) {
          // New task from server -- cache it
          await db.cachedTasks.put(serverCached);
          result.cached++;
        } else if (existing.pending_delete) {
          // Locally deleted, server DELETE not yet synced -- never resurrect.
          result.conflicts++;
          console.info(`[TaskOffline] Skipping ${serverTask.id} - pending local deletion`);
        } else if (existing.pending_changes) {
          // Local has unsaved changes -- preserve local, mark conflict
          result.conflicts++;
          console.info(`[TaskOffline] Conflict detected for ${serverTask.id} - preserving local changes`);
        } else if (new Date(serverTask.updated_at) > new Date(existing.updated_at)) {
          // Server is newer, local has no pending changes -- update from server
          await db.cachedTasks.put(serverCached);
          result.cached++;
        }
        // else: local is synced and up-to-date, no action needed
      }
    } catch (error) {
      console.warn('[TaskOffline] Failed to cache from server:', error);
      // Don't throw -- offline mode should continue to work
    }

    return result;
  }

  /**
   * Sync a single task to the server
   */
  /**
   * P2-05: a pending task that must wait rather than be sent now:
   *  - created by another user on this device (never replay it as whoever is
   *    signed in now; the drain hands it over under its originator);
   *  - a new task whose offline-created incident has not synced yet (its
   *    incident_id is not a server id the API would accept).
   */
  async isDeferred(task: CachedTask): Promise<boolean> {
    if (mustDeferSend(task)) return true;
    const isNew = !!task.offline_id && task.id === task.offline_id;
    return isNew && (await resolveSyncableIncidentId(task.incident_id)).defer;
  }

  async syncTask(taskId: string): Promise<boolean> {
    const task = await this.getById(taskId);
    if (!task || task.is_synced === 1) return true;
    if (await this.isDeferred(task)) return false;

    // Deletion tombstone: a server-backed task marked for deletion offline.
    // Push the DELETE to the server, then remove the local row. Treat a 404
    // (already gone on the server) as success.
    if (task.pending_delete) {
      try {
        await taskService.deleteTask(task.id, sendTenantOf(task));
        await db.cachedTasks.delete(task.id);
        return true;
      } catch (error: unknown) {
        const status =
          error instanceof Error && 'response' in error
            ? (error as Error & { response?: { status?: number } }).response?.status
            : undefined;
        if (status === 404) {
          // Already deleted on the server -- reconcile locally and succeed.
          await db.cachedTasks.delete(task.id);
          return true;
        }
        const message = error instanceof Error ? error.message : 'Delete sync failed';
        await db.cachedTasks.update(task.id, { last_error: message });
        console.error('[TaskOffline] Delete sync failed:', error);
        return false;
      }
    }

    try {
      // Build the create payload from the cached task (include all stored fields)
      const payload: CreateTaskData = {
        incident_id: task.incident_id || undefined,
        title: task.title,
        description: task.description,
        priority: (task.priority as TaskPriority) || 'medium',
        assigned_to: task.assigned_to || undefined,
        assigned_esf_id: task.assigned_esf_id,
        org_node_id: task.org_node_id,
        task_type_id: task.task_type_id,
        location_name: task.location_name,
        location_address: task.location_address,
        location_coordinates: task.location ? { lat: task.location.lat, lng: task.location.lon } : undefined,
        due_at: task.due_at,
        custom_fields: task.custom_fields,
        requestor_name: task.requestor_name,
        requestor_phone: task.requestor_phone,
        requestor_email: task.requestor_email,
        tags: task.tags,
        parent_task_id: task.parent_task_id,
        source_module: task.source_module,
        source_id: task.source_id,
      };

      // Check if this is a new offline task
      if (task.offline_id && task.id === task.offline_id) {
        // P2-05: send the client id (a replay returns the existing task, 200)
        // and the incident's server id if it was created offline and synced.
        const resolved = await resolveSyncableIncidentId(task.incident_id);
        if (resolved.defer) return false;
        payload.id = clientIdFor(task);
        payload.incident_id = resolved.incidentId ?? undefined;

        // Create on server
        let serverTask: { id: string };
        try {
          serverTask = await taskService.createTask(payload, sendTenantOf(task));
        } catch (createError) {
          if (isDeletedReplay(createError)) {
            // The create landed earlier and the task was deleted since: done.
            await db.cachedTasks.delete(task.id);
            return true;
          }
          throw createError;
        }

        // Replace offline record with server record (atomic to prevent data loss)
        await db.transaction('rw', db.cachedTasks, async () => {
          await db.cachedTasks.delete(task.id);
          await db.cachedTasks.put({
            ...task,
            id: serverTask.id,
            incident_id: payload.incident_id ?? null,
            is_synced: 1,
            synced_at: new Date().toISOString(),
            pending_changes: false,
            last_error: undefined,
          });
        });
      } else {
        // Update existing task on server (include all editable fields)
        await taskService.updateTask(task.id, {
          title: task.title,
          description: task.description,
          priority: task.priority as TaskPriority,
          // undefined is omitted from the payload, so the server value is
          // never accidentally cleared
          org_node_id: task.org_node_id,
          location_name: task.location_name,
          location_address: task.location_address,
          location_coordinates: task.location ? { lat: task.location.lat, lng: task.location.lon } : undefined,
          due_at: task.due_at,
          custom_fields: task.custom_fields,
          requestor_name: task.requestor_name,
          requestor_phone: task.requestor_phone,
          requestor_email: task.requestor_email,
          tags: task.tags,
        }, sendTenantOf(task));

        // Replay status and assignment through their OWN endpoints.
        // updateTaskSchema (backend task.schemas.ts) accepts neither, so these
        // are not merely omitted from the payload above — they are unreachable
        // via PATCH /tasks/:id. Before this, an offline "completed" or a field
        // reassignment was dropped here and the row was then marked synced,
        // destroying the change with no error and no pending indicator.
        if (task.pending_status) {
          await taskService.updateTaskStatus(task.id, {
            status: task.pending_status as TaskStatus,
            notes: task.pending_status_notes,
          }, sendTenantOf(task));
        }
        if (task.pending_assignment && task.assigned_to) {
          await taskService.assignTask(task.id, { assigned_to: task.assigned_to }, sendTenantOf(task));
        }

        // Only clears the pending flags if every call above succeeded — a throw
        // leaves the row unsynced for the next attempt rather than losing it.
        await db.cachedTasks.update(task.id, {
          is_synced: 1,
          synced_at: new Date().toISOString(),
          pending_changes: false,
          pending_status: undefined,
          pending_status_notes: undefined,
          pending_assignment: false,
          last_error: undefined,
        });
      }

      return true;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Sync failed';
      const isConflict = error instanceof Error &&
        'response' in error &&
        (error as Error & { response?: { status?: number } }).response?.status === 409;

      await db.cachedTasks.update(task.id, {
        last_error: isConflict ? `Conflict: ${message}` : message,
      });

      if (isConflict) {
        console.warn(`[TaskOffline] Conflict for task ${task.id}: ${message}`);
      } else {
        console.error('[TaskOffline] Sync failed:', error);
      }

      return false;
    }
  }

  /**
   * Sync all pending tasks to the server
   */
  async syncAll(): Promise<SyncResult> {
    const result: SyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      errors: [],
    };

    if (!networkService.isOnline()) {
      result.success = false;
      result.errors.push('No network connection');
      return result;
    }

    // Tenant guard: the api client injects the *ambient* X-Tenant-ID, so a
    // record created under Tenant A must not be synced while Tenant B is
    // active. Defer (leave it pending) until its own tenant is active again.
    const activeTenantId = useTenantStore.getState().activeTenant?.id;

    const pending = await this.getPending();

    for (const task of pending) {
      if (task.tenant_id != null && activeTenantId != null && task.tenant_id !== activeTenantId) {
        continue;
      }
      // Another user's item, or waiting for its offline incident: leave pending.
      if (await this.isDeferred(task)) continue;
      const success = await this.syncTask(task.id);
      if (success) {
        result.synced++;
      } else {
        result.failed++;
        result.errors.push(`Failed to sync task "${task.title}"`);
      }
    }

    result.success = result.failed === 0;
    return result;
  }

  /**
   * Clear all cached data (use with caution)
   */
  async clearCache(): Promise<void> {
    await db.cachedTasks.clear();
  }

  /**
   * Clear only synced data (keep pending)
   */
  async clearSyncedCache(): Promise<void> {
    await db.cachedTasks
      .filter(t => t.is_synced === 1 && !t.pending_changes)
      .delete();
  }
}

export const taskOfflineService = new TaskOfflineService();
